/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Alert,
  Backdrop, Box, CircularProgress, List, Snackbar, ThemeProvider, Typography, useTheme,
} from '@mui/material';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { ContentInteractionAction, MetaItem, PollInteractionAction } from '../../../common/models/content/Content';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { selectMemberSession } from '../../../store/slices/onboardingSlice';
import { QuestionOption } from '../../../common/models/Question';
import { useCustomEventTracking } from '../../../common/useCustomEventTracking';
import { removePollAnswer, selectSurveyFormAnswers } from '../../../store/slices/contentSlice';
import { SubmitSurveyFormRequest } from '../../../common/models/content/SubmitSurveyFormRequest';
import { FormAnswer } from '../../../common/models/FormAnswer';
import { deletePollAnswer, getPollTotalVotes } from '../../../api/contentAPI';
import ContentPollAnswerView from './ContentPollAnswerView';
import QuestionComponent from '../../../common/QuestionComponent';
import { ContentTheme } from './ContentTheme';
import { Answer } from '../../../common/models/Answer';
import { saveContentFormAnswers } from '../../../store/thunks/contentThunk';

export interface ContentFormQuestionProps {
  questionId: string
  questionType: string
  title?: string
  options: QuestionOption[]
  totalVotes?: number
}

export interface ContentFormProps {
  formId: string,
  question: ContentFormQuestionProps,
  contentId: number
  contentCreatorName?: string | null;
  heading?: string,
  contentMetaData?: MetaItem[]
  title?: string | null
}

export default function ContentFormView(props: ContentFormProps) {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { memberId } = useAppSelector(selectMemberSession);
  const surveyFormAnswers = useAppSelector(selectSurveyFormAnswers);
  const [loading, setLoading] = useState(false);
  const [openSuccessSnackbar, setOpenSuccessSnackbar] = useState(false);
  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);
  const [showPollResults, setShowPollResults] = useState(false);
  const [selectedOption, setSelectedOption] = useState('');
  const [totalVotes, setTotalVotes] = useState(props.question?.totalVotes || 0);
  const [answerId, setAnswerId] = useState<string>('');
  const minimumVotesToShow = 10;
  const { trackContentInteraction } = useCustomEventTracking();

  useEffect(() => {
    const pollResult =
      surveyFormAnswers.find((item) => item.question?.id === props.question?.questionId);
    if (pollResult) {
      setShowPollResults(true);
      setAnswerId(pollResult.id);
      setSelectedOption(pollResult.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.formId]);

  const updateTotalVotes = (value: number) => {
    setTotalVotes(value);
  };

  const firePollInteractionEvent = (action: PollInteractionAction, options?: {
    [k: string]: unknown;
  }) => {
    trackContentInteraction(
      action,
      'Poll',
      {
        contentId: props.contentId,
        contentTitle: props.heading || '',
        contentCreatorName: props.contentCreatorName || '',
        contentMetaData: props.contentMetaData || [],
        type: props.title || '',
        action,
        pollTitle: props.question.title,
        pollId: props.question.questionId,
        ...options,
      },
    );
  };

  const submitSurvey = (answer: Answer) => {
    setLoading(true);
    const request: SubmitSurveyFormRequest = {
      formId: props.formId,
      memberId: memberId || process.env.REACT_APP_MEMBER_ID || '',
      answers: [answer],
    };
    dispatch(saveContentFormAnswers(request)).then((res) => {
      firePollInteractionEvent(ContentInteractionAction.Respond);
      const answerResponse = res.payload as FormAnswer[];
      setLoading(false);
      setAnswerId(answerResponse[0].id); // considering poll will have only one answer
      setShowPollResults(true);
      setOpenSuccessSnackbar(true);
      setOpenErrorSnackbar(false);
    }).catch(() => {
      setLoading(false);
      setOpenSuccessSnackbar(false);
      setOpenErrorSnackbar(true);
    });
  };

  const recastVote = async () => {
    setLoading(true);
    const deletePollAnswerResponse = await deletePollAnswer(answerId);
    const getPollTotalVotesResponse = getPollTotalVotes(props.question.questionId);
    Promise.all([deletePollAnswerResponse, getPollTotalVotesResponse])
      .then(([pollAnswerResponse, totalVotesResponse]) => {
        firePollInteractionEvent(ContentInteractionAction.RecastVote);
        dispatch(removePollAnswer(answerId));
        setTotalVotes(totalVotesResponse);
        setLoading(false);
        setShowPollResults(false);
      }).catch(() => {
        setLoading(false);
        setOpenErrorSnackbar(true);
      });
  };

  const toggleOptionSelection = (event: unknown) => {
    setSelectedOption(event as string);
    const answer: Answer = {
      questionId: props.question.questionId,
      value: event as string,
    };
    submitSurvey(answer);
  };

  const getContentPollComponent = () => {
    let pollComponent;
    if (showPollResults) {
      pollComponent = <ContentPollAnswerView
        key={props.question?.questionId}
        formId={props.formId}
        questionId={props.question?.questionId}
        questionType={props.question?.questionType}
        title={props.question?.title}
        selectedOption={selectedOption}
        options={props.question?.options}
        onGetTotalVotes={updateTotalVotes}
      />;
    } else {
      pollComponent = <QuestionComponent
        type={props.question.questionType}
        options={props.question.options}
        onChange={toggleOptionSelection}
        inputValue={''}
        placeholderText={''}
      />;
    }
    return pollComponent;
  };

  const handleClose = () => {
    setOpenErrorSnackbar(false);
    setOpenSuccessSnackbar(false);
  };

  return (
    <ThemeProvider theme={ContentTheme}>
      <Box>
        <Snackbar
          open={openSuccessSnackbar}
          autoHideDuration={4000}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <Alert severity="success" sx={{ width: '100%' }}>
            {t('success.dashboard')}
          </Alert>
        </Snackbar>
        <Snackbar
          open={openErrorSnackbar}
          autoHideDuration={4000}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <Alert severity="error" sx={{ width: '100%' }}>
            {t('error.generalMessage')}
          </Alert>
        </Snackbar>
        <Backdrop
          sx={{ color: theme.palette.common.white, zIndex: () => theme.zIndex.drawer + 1 }}
          open={loading}>
          <CircularProgress color="inherit" />
        </Backdrop>
        {props.question &&
          <Box
            sx={{
              width: '100%',
            }}>
            {/* Voting must not bubble to the card-level click handler: on a locked card that
                would navigate to the membership page instead of registering the vote. */}
            <List
              sx={{ marginTop: theme.spacing(2) }}
              onClick={(e) => e.stopPropagation()}
            >
              {getContentPollComponent()}
            </List>
            <Typography
              sx={{
                cursor: 'pointer',
                textAlign: 'center',
                fontSize: theme.spacing(2.25),
                [theme.breakpoints.down('sm')]: {
                  fontSize: theme.spacing(2),
                },
              }}>
              <span
                style={{
                  cursor: 'auto',
                  fontWeight: 600,
                }}
              >{totalVotes > minimumVotesToShow && `${totalVotes} votes`}
              </span>
              {/* removing recast option for now */}
              {/* {showPollResults &&
                  totalVotes > minimumVotesToShow &&
                  <span
                    style={{
                      verticalAlign: 'text-bottom',
                      fontSize: '20px',
                    }} > . </span>
                } */}
              {/* {showPollResults &&
                <span onClick={recastVote}>
                  {t('content.poll.recast')}
                </span>
              } */}
            </Typography>
          </Box>
        }
      </Box>
    </ThemeProvider>);
}
